'use client'
import { useRef, useState, useEffect } from 'react'

const LANGS = ['en','cn','th','ru','fr','de','it','es','ja','ko','tr','he','ar']

// Optional keys added in this version (carousels without them render as before):
//   cardVariant 'luxury'   framed dark card: image (zoom on hover) with a corner chevron,
//                          uppercase title that turns accent on hover, 2-line text, arrow bottom-right.
//                          Uses cardBg, cardBorder, cardTitleColor, cardDescColor plus
//                          cardBorderHover, cardTitleHoverColor, cardAccentColor, cardMinHeight,
//                          imageAspect, cardTitleSize/Weight, cardDescSize, cardPaddingX/Y.
//                          Luxury slides size to their content (gridRowHeight is ignored).
//   cardWidth / cardWidthMobile   fixed slide width in px instead of slides-per-view
//   controlsStyle 'bottom'        round arrows + dots under the track instead of side arrows
//   controlsColor, dotActiveColor, dotColor

const Chevron = ({ size = 14, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={style}>
    <path d="m9 18 6-6-6-6" />
  </svg>
)

function CardLink({ href, lang, children }) {
  const prefix   = LANGS.includes(lang) ? `/${lang}` : ''
  const finalHref = href ? `${prefix}${href}` : '#'
  return (
    <a href={finalHref} style={{ display: 'block', width: '100%', height: '100%', textDecoration: 'none' }}>
      {children}
    </a>
  )
}

const t = (val, lang, fallback = '') =>
  !val ? fallback
  : typeof val === 'string' ? val
  : val[lang] ?? val.en ?? val[Object.keys(val)[0]] ?? fallback

function Card({ card, config, lang }) {
  const variant    = config.cardVariant ?? 'overlay'
  const bg         = card.overrideStyle ? (card.cardBg     ?? config.cardBg     ?? '#f8fafc') : (config.cardBg     ?? '#f8fafc')
  const border     = card.overrideStyle ? (card.cardBorder ?? config.cardBorder ?? '#e2e8f0') : (config.cardBorder ?? '#e2e8f0')
  const titleColor = card.overrideStyle ? (card.titleColor ?? config.cardTitleColor ?? '#0f172a') : (config.cardTitleColor ?? '#0f172a')
  const descColor  = card.overrideStyle ? (card.descColor  ?? config.cardDescColor  ?? '#64748b') : (config.cardDescColor  ?? '#64748b')
  const badgeBg    = card.overrideStyle ? (card.badgeBg    ?? config.badgeBg    ?? '#eff6ff') : (config.badgeBg    ?? '#eff6ff')
  const badgeColor = card.overrideStyle ? (card.badgeColor ?? config.badgeColor ?? '#1d4ed8') : (config.badgeColor ?? '#1d4ed8')

  const radius      = `${config.cardRadius ?? 12}px`
  const shadow      = config.cardShadow !== false ? '0 2px 12px rgba(0,0,0,0.07)' : 'none'
  const borderWidth = config.cardBorderWidth ?? 1
  const padX        = config.cardPaddingX ?? 14
  const padY        = config.cardPaddingY ?? 12
  const contentGap  = config.cardContentGap ?? 4

  const title = t(card.title, lang)
  const desc  = t(card.description, lang)
  const cta   = t(card.ctaText, lang)

  const clamp = (n) => n > 0 ? {
    display: '-webkit-box', WebkitLineClamp: n,
    WebkitBoxOrient: 'vertical', overflow: 'hidden',
  } : {}

  const ctaEl = cta ? (
    <div style={{ textAlign: config.ctaAlign ?? 'left' }}>
      <span style={{
        display: config.ctaFullWidth ? 'block' : 'inline-block',
        textAlign: 'center',
        fontSize: config.ctaFontSize ?? '0.8125rem',
        fontWeight: 600,
        padding: `${config.ctaPaddingY ?? 6}px ${config.ctaPaddingX ?? 16}px`,
        borderRadius: `${config.ctaRadius ?? 6}px`,
        background: config.ctaStyle === 'outlined' || config.ctaStyle === 'ghost'
          ? 'transparent' : (config.ctaBg ?? 'var(--color-main)'),
        color: config.ctaStyle === 'outlined' || config.ctaStyle === 'ghost'
          ? (config.ctaBg ?? 'var(--color-main)') : (config.ctaColor ?? '#ffffff'),
        border: config.ctaStyle === 'outlined'
          ? `1.5px solid ${config.ctaBg ?? 'var(--color-main)'}` : 'none',
        cursor: 'pointer',
      }}>{cta}</span>
    </div>
  ) : null

  const badgeEl = card.badge ? (
    <div style={{ textAlign: config.cardIconAlign ?? 'left' }}>
      <span style={{
        display: 'inline-block', fontWeight: 600,
        fontSize: config.badgeFontSize ?? '0.6875rem',
        padding: '3px 10px',
        borderRadius: `${config.badgeRadius ?? 20}px`,
        background: badgeBg, color: badgeColor,
      }}>{card.badge}</span>
    </div>
  ) : null

  const iconEl = card.icon ? (
    <div style={{
      fontSize: config.cardIconSize ?? '2rem',
      textAlign: config.cardIconAlign ?? 'left',
      lineHeight: 1,
    }}>{card.icon}</div>
  ) : null

  const hasText = title || desc || cta || card.badge || card.icon

  const textBody = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: contentGap }}>
      {badgeEl}
      {iconEl}
      {title && (
        <div style={{
          fontSize: config.cardTitleSize ?? '1rem',
          fontWeight: config.cardTitleWeight ?? 600,
          color: titleColor, lineHeight: 1.3,
          textAlign: config.cardTitleAlign ?? 'left',
          ...clamp(config.cardTitleClamp ?? 2),
        }}>{title}</div>
      )}
      {desc && (
        <div style={{
          fontSize: config.cardDescSize ?? '0.875rem',
          fontWeight: config.cardDescWeight ?? 400,
          color: descColor, lineHeight: 1.5,
          textAlign: config.cardDescAlign ?? 'left',
          ...clamp(config.cardDescClamp ?? 3),
        }}>{desc}</div>
      )}
      {ctaEl}
    </div>
  )

  const imgStyle = { width: '100%', height: '100%', objectFit: config.imageFit ?? 'cover', display: 'block' }
  const noImg    = <div style={{ width: '100%', height: '100%', background: '#e2e8f0' }} />

  let inner

  if (variant === 'luxury') {
    inner = (
      <div className="ccl-card" style={{
        '--ccl-border':       border,
        '--ccl-border-hover': config.cardBorderHover     ?? 'var(--thirdcolor)',
        '--ccl-title':        titleColor,
        '--ccl-title-hover':  config.cardTitleHoverColor ?? 'var(--thirdcolor)',
        '--ccl-accent':       config.cardAccentColor     ?? 'var(--thirdcolor)',
        background: bg, borderRadius: radius, borderWidth,
        minHeight: config.cardMinHeight ?? 360,
      }}>
        <div className="ccl-media" style={{ aspectRatio: config.imageAspect ?? '4/3' }}>
          {card.imageUrl && <img src={card.imageUrl} alt={title} className="ccl-img" style={{ objectFit: config.imageFit ?? 'cover' }} />}
          {config.cardCornerIcon !== false && <span className="ccl-corner"><Chevron size={14} /></span>}
        </div>
        <div className="ccl-body" style={{ padding: `${padY}px ${padX}px` }}>
          <div>
            {badgeEl}
            {title && (
              <div className="ccl-title" style={{
                fontSize: config.cardTitleSize ?? '1rem',
                fontWeight: config.cardTitleWeight ?? 600,
                textAlign: config.cardTitleAlign ?? 'left',
                ...clamp(config.cardTitleClamp ?? 2),
              }}>{title}</div>
            )}
            {desc && (
              <div style={{
                fontSize: config.cardDescSize ?? '0.875rem',
                fontWeight: config.cardDescWeight ?? 400,
                color: descColor, lineHeight: 1.625,
                textAlign: config.cardDescAlign ?? 'left',
                ...clamp(config.cardDescClamp ?? 2),
              }}>{desc}</div>
            )}
            {ctaEl}
          </div>
          {config.cardArrow !== false && (
            <div className="ccl-foot"><Chevron size={16} style={{ display: 'block' }} /></div>
          )}
        </div>
      </div>
    )
  } else if (variant === 'overlay') {
    inner = (
      <div style={{
        position: 'relative', borderRadius: radius, overflow: 'hidden',
        boxShadow: shadow, width: '100%', height: '100%',
      }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {card.imageUrl
            ? <img src={card.imageUrl} alt={title} style={imgStyle} />
            : noImg
          }
        </div>
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to top, ${config.overlayEnd ?? 'rgba(0,0,0,0.7)'}, ${config.overlayStart ?? 'rgba(0,0,0,0)'})`,
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          padding: `${padY}px ${padX}px`,
        }}>
          {badgeEl}
          {title && (
            <div style={{
              fontSize: config.cardTitleSize ?? '1rem',
              fontWeight: config.cardTitleWeight ?? 600,
              color: '#fff', lineHeight: 1.3,
              textAlign: config.cardTitleAlign ?? 'left',
              marginBottom: 3,
              ...clamp(config.cardTitleClamp ?? 2),
            }}>{title}</div>
          )}
          {desc && (
            <div style={{
              fontSize: config.cardDescSize ?? '0.875rem',
              color: 'rgba(255,255,255,0.8)', lineHeight: 1.4,
              textAlign: config.cardDescAlign ?? 'left',
              ...clamp(config.cardDescClamp ?? 3),
            }}>{desc}</div>
          )}
          {ctaEl}
        </div>
      </div>
    )
  } else if (variant === 'default') {
    inner = (
      <div style={{
        background: bg, border: `${borderWidth}px solid ${border}`,
        borderRadius: radius, overflow: 'hidden', boxShadow: shadow,
        display: 'flex', flexDirection: 'column',
        width: '100%', height: '100%',
      }}>
        <div style={{ overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {card.imageUrl ? <img src={card.imageUrl} alt={title} style={imgStyle} /> : noImg}
        </div>
        {hasText && (
          <div style={{ padding: `${padY}px ${padX}px`, flexShrink: 0 }}>
            {textBody}
          </div>
        )}
      </div>
    )
  } else if (variant === 'horizontal') {
    inner = (
      <div style={{
        background: bg, border: `${borderWidth}px solid ${border}`,
        borderRadius: radius, overflow: 'hidden', boxShadow: shadow,
        display: 'flex', width: '100%', height: '100%',
      }}>
        <div style={{ width: '40%', flexShrink: 0, overflow: 'hidden' }}>
          {card.imageUrl ? <img src={card.imageUrl} alt={title} style={imgStyle} /> : noImg}
        </div>
        <div style={{ padding: `${padY}px ${padX}px`, flex: 1, display: 'flex', alignItems: 'center' }}>
          {textBody}
        </div>
      </div>
    )
  } else {
    // minimal
    inner = (
      <div style={{
        background: bg, border: `${borderWidth}px solid ${border}`,
        borderRadius: radius, boxShadow: shadow,
        padding: `${padY}px ${padX}px`,
        display: 'flex', flexDirection: 'column',
        width: '100%', height: '100%',
      }}>
        {textBody}
      </div>
    )
  }

  return card.url
    ? <CardLink href={card.url} lang={lang}>{inner}</CardLink>
    : <div style={{ width: '100%', height: '100%' }}>{inner}</div>
}

// ── Grid ──────────────────────────────────────────────────────────────────────
function CardGrid({ entries, config, lang }) {
  const cols       = config.gridCols       ?? 3
  const colsTablet = config.gridColsTablet ?? 2
  const colsMobile = config.gridColsMobile ?? 1
  const rowHeight  = config.gridRowHeight  ?? 220
  const gap        = config.cardGap        ?? 16
  const uid        = 'ccg'

  const flatCards = entries.flatMap(e =>
    e.type === 'stack'
      ? e.cards.map(c => ({ ...c, rowSpan: 1 }))
      : [{ ...e.cards[0], rowSpan: e.cards[0]?.rowSpan ?? 1 }]
  )

  return (
    <>
      <style>{`
        .${uid} {
          display: grid;
          gap: ${gap}px;
          grid-template-columns: repeat(${colsMobile}, 1fr);
          grid-auto-rows: ${rowHeight}px;
        }
        @media (min-width: 768px)  { .${uid} { grid-template-columns: repeat(${colsTablet}, 1fr); } }
        @media (min-width: 1024px) { .${uid} { grid-template-columns: repeat(${cols}, 1fr); } }
      `}</style>
      <div className={uid}>
        {flatCards.map(card => (
          <div key={card.id} style={{ gridRow: `span ${card.rowSpan ?? 1}`, overflow: 'hidden' }}>
            <Card card={card} config={config} lang={lang} />
          </div>
        ))}
      </div>
    </>
  )
}

// ── Carousel ──────────────────────────────────────────────────────────────────
function CardCarousel({ entries, config, lang }) {
  const trackRef              = useRef(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(true)
  const [activeDot, setActiveDot] = useState(0)

  const autoHeight   = config.cardVariant === 'luxury'
  const fixedWidth   = !!config.cardWidth
  const bottomCtrls  = config.controlsStyle === 'bottom'

  const gap           = config.cardGap      ?? 16
  const rowHeight     = config.gridRowHeight ?? 220
  const desktopSlides = config.cardsDesktop  ?? 3
  const tabletSlides  = config.cardsTablet   ?? 2
  const mobileSlides  = config.cardsMobile   ?? 1.2

  const maxStackSize     = entries.reduce((max, e) => e.type === 'stack' ? Math.max(max, e.cards.length) : max, 1)
  const stackTotalHeight = maxStackSize * rowHeight + (maxStackSize - 1) * gap

  const updateButtons = () => {
    const el = trackRef.current
    if (!el) return
    setCanPrev(el.scrollLeft > 4)
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
    const max = el.scrollWidth - el.clientWidth
    setActiveDot(max > 0 ? Math.round((el.scrollLeft / max) * (entries.length - 1)) : 0)
  }

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    updateButtons()
    el.addEventListener('scroll', updateButtons, { passive: true })
    return () => el.removeEventListener('scroll', updateButtons)
  }, [entries])

  const scroll = (dir) => {
    const el = trackRef.current
    if (!el) return
    const step = fixedWidth && el.firstElementChild
      ? el.firstElementChild.getBoundingClientRect().width + gap
      : el.clientWidth / desktopSlides + gap
    el.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  const goTo = (i) => {
    const el = trackRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    el.scrollTo({ left: entries.length > 1 ? (max * i) / (entries.length - 1) : 0, behavior: 'smooth' })
  }

  const btnBase = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    zIndex: 10, width: 36, height: 36, borderRadius: '50%',
    border: '1px solid #e2e8f0', background: '#ffffff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, color: '#0f172a', lineHeight: 1,
  }

  const cssVars = {
    '--slides-mobile':  mobileSlides,
    '--slides-tablet':  tabletSlides,
    '--slides-desktop': desktopSlides,
    '--card-gap':       `${gap}px`,
    ...(fixedWidth ? {
      '--card-w':        `${config.cardWidth}px`,
      '--card-w-mobile': `${config.cardWidthMobile ?? config.cardWidth}px`,
    } : {}),
  }

  const ctrlColor = config.controlsColor ?? '#57534e'
  const roundBtn = (disabled) => ({
    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid rgba(231,229,228,0.8)', background: 'rgba(255,255,255,0.9)', color: ctrlColor,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.3 : 1, padding: 0, transition: 'all .2s ease',
  })

  return (
    <div className={fixedWidth ? 'cc-fixed' : undefined} style={{ position: 'relative', ...cssVars }}>
      <style>{`
        .cc-track::-webkit-scrollbar { display: none; }
        .cc-slide {
          flex: 0 0 calc((100% - var(--card-gap) * (var(--slides-mobile) - 1)) / var(--slides-mobile));
          scroll-snap-align: start;
          min-width: 0;
        }
        @media (min-width: 768px) {
          .cc-slide { flex: 0 0 calc((100% - var(--card-gap) * (var(--slides-tablet) - 1)) / var(--slides-tablet)); }
        }
        @media (min-width: 1024px) {
          .cc-slide { flex: 0 0 calc((100% - var(--card-gap) * (var(--slides-desktop) - 1)) / var(--slides-desktop)); }
        }
        .cc-fixed .cc-slide { flex: 0 0 var(--card-w-mobile); }
        @media (min-width: 768px) { .cc-fixed .cc-slide { flex: 0 0 var(--card-w); } }
      `}</style>

      {canPrev && !bottomCtrls && (
        <button onClick={() => scroll(-1)} style={{ ...btnBase, left: -18 }} aria-label="Previous">‹</button>
      )}

      <div ref={trackRef} className="cc-track" style={{
        display: 'flex', gap, overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none', msOverflowStyle: 'none',
        alignItems: autoHeight ? 'stretch' : 'flex-start',
        padding: autoHeight ? '8px 0' : undefined,
      }}>
        {entries.map(entry => {
          const isStack = entry.type === 'stack'
          const cards   = entry.cards ?? []

          if (isStack) {
            return (
              <div key={entry.id} className="cc-slide" style={{
                height: stackTotalHeight,
                display: 'flex', flexDirection: 'column', gap,
              }}>
                {cards.map(card => (
                  <div key={card.id} style={{ height: rowHeight, flexShrink: 0, overflow: 'hidden' }}>
                    <Card card={card} config={config} lang={lang} />
                  </div>
                ))}
              </div>
            )
          }

          return (
            <div key={entry.id} className="cc-slide" style={autoHeight ? { display: 'flex' } : { height: stackTotalHeight, overflow: 'hidden' }}>
              <Card card={cards[0]} config={config} lang={lang} />
            </div>
          )
        })}
      </div>

      {canNext && !bottomCtrls && (
        <button onClick={() => scroll(1)} style={{ ...btnBase, right: -18 }} aria-label="Next">›</button>
      )}

      {bottomCtrls && entries.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 10 }}>
          <button type="button" onClick={() => scroll(-1)} disabled={!canPrev} aria-label="Scroll left" style={roundBtn(!canPrev)}>
            <Chevron size={12} style={{ transform: 'scaleX(-1)' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
            {entries.map((e, i) => (
              <button key={e.id ?? i} type="button" onClick={() => goTo(i)} aria-label={`Go to slide ${i + 1}`}
                style={{
                  width: i === activeDot ? 18 : 7, height: i === activeDot ? 6 : 7, borderRadius: 999,
                  background: i === activeDot ? (config.dotActiveColor ?? '#27272a') : (config.dotColor ?? '#d6d3d1'),
                  border: 0, padding: 0, cursor: 'pointer', flexShrink: 0, transition: 'all .2s ease',
                }} />
            ))}
          </div>
          <button type="button" onClick={() => scroll(1)} disabled={!canNext} aria-label="Scroll right" style={roundBtn(!canNext)}>
            <Chevron size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

// Styles for cardVariant 'luxury' (hover states need real CSS)
const LUXURY_CSS = `
  .ccl-card { display: flex; flex-direction: column; flex: 1; width: 100%; height: 100%; overflow: hidden; box-sizing: border-box;
    border-style: solid; border-color: var(--ccl-border); transition: border-color .3s ease, box-shadow .3s ease; }
  .ccl-card:hover { border-color: var(--ccl-border-hover); box-shadow: 0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1); }
  .ccl-media { position: relative; width: 100%; flex-shrink: 0; overflow: hidden; background: #1c1917; }
  .ccl-img { position: absolute; inset: 0; width: 100%; height: 100%; display: block; transition: transform .5s ease-out; }
  .ccl-card:hover .ccl-img { transform: scale(1.05); }
  .ccl-corner { position: absolute; top: 12px; right: 12px; z-index: 1; display: flex; padding: 4px; border-radius: 2px;
    background: rgba(0,0,0,.4); -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
    border: 1px solid rgba(201,169,110,.3); color: var(--ccl-accent); }
  .ccl-body { display: flex; flex-direction: column; flex: 1; justify-content: space-between; text-align: left; }
  .ccl-title { color: var(--ccl-title); text-transform: uppercase; letter-spacing: .05em; line-height: 1.4; margin-bottom: 6px; transition: color .3s ease; }
  .ccl-card:hover .ccl-title { color: var(--ccl-title-hover); }
  .ccl-foot { display: flex; justify-content: flex-end; align-items: center; padding-top: 12px; margin-top: auto; color: var(--ccl-accent); }
  .ccl-foot svg { transition: transform .3s ease; }
  .ccl-card:hover .ccl-foot svg { transform: translateX(4px); }
  @media (max-width: 639px) {
    .ccl-title { font-size: 0.875rem !important; }
  }
`

// ── Main export ───────────────────────────────────────────────────────────────
export default function CardCarouselSlider({ config, lang }) {
  const entries     = config.entries ?? []
  const isCarousel  = (config.displayMode ?? 'carousel') === 'carousel'
  const title       = t(config.title, lang)
  const desc        = t(config.desc, lang)
  const viewAllText = t(config.viewAllText, lang, 'View all')

  if (!entries.length) return null

  const prefix      = LANGS.includes(lang) ? `/${lang}` : ''
  const viewAllHref = config.viewAllUrl ? `${prefix}${config.viewAllUrl}` : '#'

  const titleEl = title && (
    <h2 style={{
      fontSize: config.titleSize ?? '1.75rem',
      fontWeight: config.titleWeight ?? 700,
      color: config.titleColor ?? '#1a1a1a',
      textAlign: config.titleAlign ?? 'left',
      margin: 0,
    }}>{title}</h2>
  )

  const descEl = config.showDesc && desc && (
    <p style={{
      fontSize: config.descSize ?? '0.9rem',
      color: config.descColor ?? '#6b6b6b',
      textAlign: config.titleAlign ?? 'left',
      marginTop: title ? '0.5rem' : 0,
      marginBottom: 0,
    }}>{desc}</p>
  )

  const header = (title || descEl) && (
    <div className='carousel-title-align' style={{ marginBottom: config.headerGap ?? 24 }}>
      {config.showViewAll && config.viewAllUrl
        ? <a href={viewAllHref} style={{ textDecoration: 'none' }}>{titleEl}</a>
        : titleEl
      }
      {descEl}
    </div>
  )

  const viewAllLink = config.showViewAll && config.viewAllUrl && (
    <div style={{ textAlign: 'right', marginTop: 16 }}>
      <a href={viewAllHref} style={{ fontSize: 13, color: 'var(--color-main)', fontWeight: 600, textDecoration: 'none' }}>
        {viewAllText} →
      </a>
    </div>
  )

  return (
    <section className='carousel-wrapper lg:pl-0 pl-3'>
      {config.cardVariant === 'luxury' && <style>{LUXURY_CSS}</style>}
      {header}
      {isCarousel
        ? <CardCarousel entries={entries} config={config} lang={lang} />
        : <CardGrid     entries={entries} config={config} lang={lang} />
      }
      {viewAllLink}
    </section>
  )
}